'use client';

import { ENUM_UTILS, MarketId } from '@velocity-exchange/common';
import { useCallback, useEffect, useRef } from 'react';
import { VelocityWindow } from 'src/window/velocityWindow';
import { dlog } from '../../dev';
import Env from '../../environmentVariables/EnvironmentVariables';
import useVelocityStore from '../../stores/VelocityStore/useVelocityStore';
import useMarketStateStore, {
	MarketStateStore,
} from '../../stores/useMarketStateStore';
import SuccessAndFailureBuffer from '../../utils/SuccessAndFailureBuffer/SuccessAndFailureBuffer';
import useCurrentSlotRef from '../useCurrentSlotRef';
import useMarketsForDlobToTrack, {
	MarketStatePriority,
} from '../useMarketsForDlobToTrack';
import { usePageVisibility } from '../utils/usePageVisibility';
import { useAllTradeableMarketIds } from '../utils/useAllTradeableMarketIds';
import {
	BACKGROUND_DEEP_TICK_INTERVAL_MULTIPLIER,
	BACKGROUND_SHALLOW_TICK_INTERVAL_MULTIPLIER,
	BASE_TICK_INTERVAL_MS,
} from './useSyncMarketDataWithDlobServerPolling';
import { SlotDurationMs, msToSlotsNum } from '@velocity-exchange/sdk';
import useOrderbookDisplayStore, {
	OrderbookSource,
} from 'src/stores/orderbook/useOrderbookDisplayStore';
import { useRiskCeilingSlotClock } from '../useSlotClock';
import useSyncedRef from '../utils/useSyncedRef';

const APP_WIDE_ALLOWED_INITIAL_DELAY_MS = Env.initialDataSourceMaxDelay; // We allow this much time during setup for the first message to come through
const NEW_SUBSCRIPTION_ALLOWED_INITIAL_DELAY_MS = Env.newDataSourceSubMaxDelay; // This is how much time a message is allowed to come through when the app has already been setup and we're subscribing to a new market

const SLOT_SUBSCRIBER_COMMITMENT_OFFSET = 10; // Slot subscriber can only use processed commitment, UI uses finalized, which now reports the same slot as confirmed, so the 10 slot offset is unchanged.
const STALE_DATA_CUTOFF_MS = 20_000;

const USE_STRICT_FALLBACK = false; // If this is enabled then the fallback will be triggered instantly when staleness is detected, otherwise it uses uses the failure buffer and only falls back after FALLBACK_FAILURES_IN_A_ROW number of failures

const FALLBACK_FAILURES_IN_A_ROW = 3;

const UPGRADE_DELAY_MS = Env.upgradeDataSourceDelay;

const HEALTH_CHECK_FREQUENCY_MS = 2 * 1000; // 2 seconds

const APP_START_TIME = Date.now();

// We rely on the subscription tick interval instead of simply hardcoding the number of slots,
// because it affects the allowable staleness of DLOB one way or another. Hence, we give them
// dependencies here in hopes of any author updating the subscription tick intervals will also update
// the staleness config if needed.
const PRIORITY_STALENESS_TOLERANCE_MS: Record<MarketStatePriority, number> = {
	[MarketStatePriority.SelectedMarket]: STALE_DATA_CUTOFF_MS,
	// background polling is fallback only, 10s interval
	[MarketStatePriority.BackgroundDeep]:
		BASE_TICK_INTERVAL_MS * BACKGROUND_DEEP_TICK_INTERVAL_MULTIPLIER * 10,
	[MarketStatePriority.BackgroundShallow]:
		BASE_TICK_INTERVAL_MS * BACKGROUND_SHALLOW_TICK_INTERVAL_MULTIPLIER * 2.2,
};

/**
 * The tolerances above are wall-clock, so they convert at the live slot length
 * and floor (a risk ceiling tightens rather than accepts stale data). The
 * commitment offset stays in slots: it is a slot-space lag between `processed`
 * and `finalized`, which is ratio-invariant.
 */
const getStalenessThresholdSlots = (
	marketStatePriority: MarketStatePriority,
	slotDurationMs: SlotDurationMs
) => {
	const toleranceSlots = msToSlotsNum(
		PRIORITY_STALENESS_TOLERANCE_MS[marketStatePriority],
		slotDurationMs
	);

	return marketStatePriority === MarketStatePriority.SelectedMarket
		? toleranceSlots + SLOT_SUBSCRIBER_COMMITMENT_OFFSET
		: toleranceSlots;
};

const useAllMarketIdsToCheckHealthFor = () => {
	const marketsByCategories = useMarketsForDlobToTrack().categories;

	return marketsByCategories;
};

const dataHealthCheck = (
	marketId: MarketId,
	marketStatePriority: MarketStatePriority,
	getMarketStateForMarket: MarketStateStore['getMarketDataForMarket'],
	getSubscriptionStateForMarket: MarketStateStore['getSubscriptionStateForMarket'],
	handleFallback: (marketId: MarketId) => void,
	getLatestSlot: () => number,
	successAndFailureBuffer: SuccessAndFailureBuffer,
	slotDurationMs: SlotDurationMs
) => {
	const currentMarketState = getMarketStateForMarket(marketId);
	const currentSubscriptionState = getSubscriptionStateForMarket(marketId);

	if (Date.now() - APP_START_TIME < APP_WIDE_ALLOWED_INITIAL_DELAY_MS) {
		return;
	}

	if (
		!currentSubscriptionState ||
		Date.now() - currentSubscriptionState.lastSubscriptionChangeTime <
			NEW_SUBSCRIPTION_ALLOWED_INITIAL_DELAY_MS
	) {
		return;
	}

	const latestOnChainSlot = getLatestSlot();
	const latestUpdateSlot = currentMarketState?.derivedState?.updateSlot ?? 0;
	const diff = latestOnChainSlot - latestUpdateSlot;

	const stalenessThreshold = getStalenessThresholdSlots(
		marketStatePriority,
		slotDurationMs
	);
	const isStale = diff >= stalenessThreshold;

	if (isStale) {
		dlog(
			`market_subscriptions`,
			`${marketId.key} STALE => latestOnChainSlot: ${latestOnChainSlot} latestUpdateSlot: ${latestUpdateSlot} diff: ${diff}`
		);

		if (USE_STRICT_FALLBACK) {
			handleFallback(marketId);
			return;
		} else {
			successAndFailureBuffer.addResult('failure');
		}
	} else {
		if (USE_STRICT_FALLBACK) {
			// Do nothing
		} else {
			successAndFailureBuffer.addResult('success');
		}
	}

	if (!successAndFailureBuffer.isSuccess) {
		dlog(
			`market_subscriptions`,
			`auto_switch_fallback ${marketId.key} => TOO MUCH STALENESS`
		);
		handleFallback(marketId);
	}
};

const generateSuccessFailureBuffer = () => {
	return new SuccessAndFailureBuffer(
		FALLBACK_FAILURES_IN_A_ROW,
		(_bufferSize, _successCount, failureCount) => {
			return !(failureCount >= FALLBACK_FAILURES_IN_A_ROW);
		}
	);
};

/**
 * This hook is responsible for detecting when the current dlob data source is stale or not,
 * and switches to the fallback datasource if it is.
 */
const useDlobSourceFallback = () => {
	const allTradeableMarketIds = useAllTradeableMarketIds();
	const fallbackDatasource = useMarketStateStore((s) => s.fallbackDatasource);
	const orderbookSource = useOrderbookDisplayStore((s) => s.orderbookSource);
	const currentSlotRef = useCurrentSlotRef();
	// Held in a ref so a gate flip (or the feed dropping to its fallback) doesn't
	// tear down and rebuild the health-check interval.
	const slotDurationMsRef = useSyncedRef(
		useRiskCeilingSlotClock().slotDurationMs
	);

	const pageIsVisible = usePageVisibility();

	const minorHealthCheckFailureBuffer = useRef<
		Map<string, SuccessAndFailureBuffer>
	>(
		allTradeableMarketIds.reduce((lookup, marketId) => {
			lookup.set(marketId.key, generateSuccessFailureBuffer());

			return lookup;
		}, new Map())
	);

	useEffect(() => {
		allTradeableMarketIds.forEach((marketId) => {
			minorHealthCheckFailureBuffer.current.set(
				marketId.key,
				generateSuccessFailureBuffer()
			);
		});
	}, [allTradeableMarketIds.length]);

	const getLastUpdateSlot = () => {
		return currentSlotRef.current;
	};

	const selectedMarketId = useVelocityStore(
		(s) => s.selectedMarket.current?.marketId
	);

	const getMarketStateForMarket = useMarketStateStore(
		(s) => s.getMarketDataForMarket
	);

	const getSubscriptionStateForMarket = useMarketStateStore(
		(s) => s.getSubscriptionStateForMarket
	);

	const handleFallback = useCallback(
		(marketId: MarketId) => {
			// we don't want market data to fallback to blockchain unless the orderbook has, indicating that the dlob-server is completely cooked
			if (!ENUM_UTILS.match(orderbookSource, OrderbookSource.BLOCKCHAIN)) {
				return;
			}

			minorHealthCheckFailureBuffer.current.get(marketId.key)?.reset();
			fallbackDatasource(marketId);
		},
		[fallbackDatasource, minorHealthCheckFailureBuffer, orderbookSource]
	);

	const marketsByCategories = useAllMarketIdsToCheckHealthFor();

	useEffect(() => {
		// # Check if the currently fetched data is stale.
		if (!pageIsVisible) {
			dlog(
				`market_subscriptions`,
				`skipping_auto_switch_fallback => tab_hidden`
			);
			// Data is allowed to be stale when the UI is idle
			return;
		}

		const handleDataHealthCheckForMarketId =
			(marketStatePriority: MarketStatePriority) => (marketId: MarketId) => {
				let marketIdSuccessAndFailureBuffer =
					minorHealthCheckFailureBuffer.current.get(marketId.key);

				if (!marketIdSuccessAndFailureBuffer) {
					minorHealthCheckFailureBuffer.current.set(
						marketId.key,
						generateSuccessFailureBuffer()
					);
					marketIdSuccessAndFailureBuffer =
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						minorHealthCheckFailureBuffer.current.get(marketId.key)!;
				}

				dataHealthCheck(
					marketId,
					marketStatePriority,
					getMarketStateForMarket,
					getSubscriptionStateForMarket,
					handleFallback,
					getLastUpdateSlot,
					marketIdSuccessAndFailureBuffer,
					slotDurationMsRef.current
				);
			};

		const interval = setInterval(() => {
			if (VelocityWindow.DISABLE_AUTO_SWITCH_DLOB_SOURCE) return;

			Object.values(MarketStatePriority).forEach((priority) => {
				marketsByCategories[priority]?.forEach(
					handleDataHealthCheckForMarketId(priority)
				);
			});
		}, HEALTH_CHECK_FREQUENCY_MS);

		return () => {
			clearInterval(interval);
		};
	}, [selectedMarketId, marketsByCategories, orderbookSource]);
};

/**
 * This hook is responsible for automatically switching to the best source of dlob.
 * For now it implements a simple logic to switch to the best possible source, and
 * allow it to fallback down by itself (handled by useDlobFallback)
 */
const useDlobSourceUpgrade = () => {
	const allTradeableMarketIds = useAllTradeableMarketIds();
	const handleUpgradeDatasource = useMarketStateStore(
		(s) => s.upgradeDatasource
	);

	const getMarketSubscriptionState = useMarketStateStore(
		(s) => s.getSubscriptionStateForMarket
	);

	useEffect(() => {
		const upgradeAttemptInterval = setInterval(() => {
			if (VelocityWindow.DISABLE_AUTO_SWITCH_DLOB_SOURCE) return;

			for (const marketId of allTradeableMarketIds) {
				const marketSubscriptionState = getMarketSubscriptionState(marketId);

				if (!marketSubscriptionState) {
					// In the upgrade logic we can ignore markets that don't have any subscription state. It's possible that the market is just a delisted one (or something similar) where we expect there to be no state. The FALLBACK logic will handle any markets which aren't successfully fetching any state when we expect them to
					continue;
				}

				if (
					marketSubscriptionState.listeningSelection ===
					marketSubscriptionState.preferredListeningSelection
				) {
					// no need to upgrade if the listening selection is the same as the preferred listening selection
					continue;
				}

				const _upgradeAttemptCount =
					marketSubscriptionState.upgradeAttemptCount;
				const subscriptionStartTime =
					marketSubscriptionState.lastSubscriptionChangeTime;

				// const upgradeDelay = UPGRADE_DELAY_MS * 2 ** upgradeAttemptCount;
				const upgradeDelay = UPGRADE_DELAY_MS; // Presently looks like fallbacks to the blockchain are happening semi-frequently from dlob going stale. We really want to avoid staying in this state so removing the exponential backoff on connection upgrades for now.

				if (Date.now() - subscriptionStartTime < upgradeDelay) {
					continue;
				}

				handleUpgradeDatasource(marketId);
			}
		}, 1000 * 5);

		return () => {
			clearInterval(upgradeAttemptInterval);
		};
	}, []);
};

const useDlobAutoSwitchSource = () => {
	useDlobSourceFallback();
	useDlobSourceUpgrade();
};

export default useDlobAutoSwitchSource;
